/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * Licensed under The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */
import { ComponentChildren } from 'preact';
import type { ViewportLayout } from './utils/dataLayoutUtils';
import { getTaskAriaLabel } from './utils/ariaUtils';
import { useTranslationBundle } from '#hooks/UNSAFE_useTranslationBundle';
import { BundleType } from '#resources/nls/bundle';
import { taskMobileAccStyles } from './themes/GanttStyles.css';
import type { GanttRowData, GanttTaskData } from './gantt.types';

type Props<K1, K2, D1 extends GanttRowData<K1, D1, D2>, D2 extends GanttTaskData<K2>> = {
  width: number;
  height: number;
  rowData: ViewportLayout<K1, K2, D1, D2>['rowData'];
  dateFormatter: (date: string) => string;
  layoutObjs: ViewportLayout<K1, K2, D1, D2>['rowObjs'];
};

export function GanttMobileTaskAccContent<
  K1,
  K2,
  D1 extends GanttRowData<K1, D1, D2>,
  D2 extends GanttTaskData<K2>
>({ width, height, rowData, dateFormatter, layoutObjs }: Props<K1, K2, D1, D2>) {
  const translations = useTranslationBundle<BundleType>('@oracle/oraclejet-preact');
  const content: ComponentChildren[] = [];
  layoutObjs.forEach((rowObj) => {
    rowObj.taskObjs.forEach((taskObj) => {
      const ariaLabel = getTaskAriaLabel(taskObj, rowData, dateFormatter, translations);
      content.push(
        <div
          role="img"
          aria-label={ariaLabel}
          class={taskMobileAccStyles.content}
          style={{
            width: taskObj.width,
            height: taskObj.height,
            top: taskObj.y,
            left: taskObj.x
          }}></div>
      );
    });
  });

  return (
    <div class={taskMobileAccStyles.base} style={{ width, height }}>
      {content}
    </div>
  );
}
